import { useState } from "react";
import api from "@/lib/api";
import { Loader2, Upload as UploadIcon, X } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_MAX_BYTES = 10 * 1024 * 1024; // matches backend cap for /files/upload

export default function FileUpload({
  onUploaded,
  accept,
  label = "Click to upload (max 10MB)",
  testid = "file-upload",
  authed = true,
  maxMB,
  endpoint,
}) {
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);

  const maxBytes = maxMB ? maxMB * 1024 * 1024 : DEFAULT_MAX_BYTES;

  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > maxBytes) { toast.error(`File exceeds ${maxMB || 10}MB`); return; }
    setBusy(true);
    setFile(f);
    try {
      const form = new FormData();
      form.append("file", f);
      // Sign-up flow is unauthenticated — use the public endpoint that mints a
      // pending_signup_link=true file row the registration call can claim.
      const url = endpoint || (authed ? "/files/upload" : "/auth/signup-upload");
      const { data } = await api.post(url, form, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded?.(data);
      toast.success("Uploaded");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
      setFile(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <label data-testid={testid} className="flex items-center gap-3 border border-dashed border-slate-300 rounded-md p-3 cursor-pointer hover:border-cyan-500 hover:bg-cyan-50/30 transition-colors">
      <input type="file" className="hidden" accept={accept} onChange={handle} />
      {busy ? <Loader2 className="w-4 h-4 animate-spin text-cyan-600" /> : <UploadIcon className="w-4 h-4 text-slate-500" />}
      <span className="text-sm text-slate-600">{file ? file.name : label}</span>
      {file && !busy && (
        <X className="w-4 h-4 ml-auto text-slate-400" onClick={(e) => { e.preventDefault(); setFile(null); }} />
      )}
    </label>
  );
}
